import { OneClick } from '@/components/blocks/OneClick';
import { OrderItemTableRowData } from '@/components/content/OrderItemTable/parts/Table';
import { orderItemTableIconSX } from '@/components/content/OrderItemTable/styles/orderItemTableIcon';
import { orderItemTableItemPaintSX } from '@/components/content/OrderItemTable/styles/orderItemTableItemPaint';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useCurrencyFormat } from '@/data/Content/CurrencyFormat';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTableRow';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { ContentContext } from '@/data/context/content';
import { EventsContext } from '@/data/context/events';
import { formatPrice } from '@/utils/formatPrice';
import { Stack, Typography } from '@mui/material';
import { FC, MouseEvent, useContext } from 'react';
import { LOCALE_ES_MX, MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

export const ItemPaintDetails: FC = () => {
	const labels = useLocalization('HDMProductDetail');
	const { decimalPlaces } = useCurrencyFormat();
	const {
		details,
		quantity: { quantity, onChange },
		price,
	} = useContext(ContentContext) as OrderItemTableRowData & ReturnType<typeof useOrderItemTableRow>;
	const {
		prices: { currency = DEFAULT_CURRENCY },
	} = details;
	const { orderItemPrice } = price;
	const { onRemoveFromCart } = useContext(EventsContext);
	const { settings } = useSettings();

	const removeFromCart = async (event: MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		await onChange(0);

		onRemoveFromCart({
			gtm: {
				partNumber: details.partNumber,
				name: details.name,
				price: `${price.orderItemPrice}`,
				currency: price.currency,
				quantity: `${quantity}`,
				seller: details?.seller,
				sellerId: details?.sellerId,
				orgName: '',
				orgId: '',
				storeName: settings.storeName,
				settings,
			},
		});
	};

	return (
		<Stack
			direction="row"
			justifyContent="space-between"
			sx={orderItemTableItemPaintSX.container}
			alignItems="center"
		>
			<Stack direction="row" alignItems="center">
				<OneClick
					wrapper="icon"
					onClick={removeFromCart}
					spin={true}
					spinSize={24}
					sx={{ color: 'secondary.main' }}
					className="quick-cart__delete-product"
				>
					<DynamicIcon name={MATERIAL_SYMBOL_ICONS.delete} sx={orderItemTableIconSX} />
				</OneClick>
				<Typography
					variant="bodySubText"
					sx={{ color: 'grey.200' }}
					className="quick-cart__product-quantity"
				>
					{labels.Quantity.t()} {quantity}
				</Typography>
			</Stack>
			<Typography
				data-testid="item-price"
				id="item-price"
				variant="bodySubText"
				sx={orderItemTableItemPaintSX.semibold}
				className="quick-cart__product-price"
			>
				{formatPrice(LOCALE_ES_MX, currency, orderItemPrice, decimalPlaces)}
			</Typography>
		</Stack>
	);
};
